/* tslint:disable:max-line-length */
import {Injectable} from '@angular/core';
import {BasedataService} from '@docma-mm-core/shared/basedata.service';
import {UntilDestroy} from '@ngneat/until-destroy';
import {FaultsImportService} from '../../faults-import.service';
import {ConfigService} from '@docma-mm-core/config/config.service';
import {MydocmaDatePipe, MydocmaNumberPipe} from '@mydocma/common';
import {HttpClient} from '@angular/common/http';
import {TranslateService} from '@ngx-translate/core';
import {CellValidationResult, ValidationResult} from '../common/cell-validate-import.result';
import {FieldMinimal} from '@docma-mm-core/field/field';
import {FieldDataTypeEnum} from '@docma-mm-core/field/field-data-type.enum';
import {ColDef, ValueFormatterParams} from 'ag-grid-community';
import {FieldDefaultNameEnum} from '@docma-mm-core/field/field-default-name.enum';
import {getLocaleNumberSymbol, NumberSymbol} from '@angular/common';
import {ValidationErrorCodes} from '../common/validation-error-codes';
import {ValidStatusCellRendererComponent} from '../CellRenderer/valid-status.cell-renderer/valid-status.cell-renderer.component';
import {ValidTooltipComponent} from '../steps/validate-import/valid-tooltip/valid-tooltip.component';
import {ValidFilterComponent} from '../steps/validate-import/valid-filter/valid-filter.component';
import {ImportConstants} from '../common/import-constants';
import {CheckboxCellRenderer} from '../../../mm-faults-ag-grid/cell-renderers/checkbox.cell-renderer/checkbox.cell-renderer';
import {fieldDefaultsImport} from '../../../mm-faults-ag-grid/col-defs/field-defaults';

@UntilDestroy()
@Injectable()
export class BaseImportService extends BasedataService {

  constructor(protected faultsImportService: FaultsImportService,
              protected translateService: TranslateService,
              protected datepipe: MydocmaDatePipe,
              protected numberpipe: MydocmaNumberPipe,
              protected http: HttpClient,
              protected configService: ConfigService) {
    super(configService);
  }

  public async getColDefs(validationResult: any, previewMode = false) {
    const colDefs: ColDef[] = [];

    for (const fieldName of Object.keys(validationResult[0])) {

      if (this.faultsImportService.fieldDict.hasOwnProperty(fieldName)) {
        const field = this.faultsImportService.fieldDict[fieldName];
        const defaultWidth = (fieldDefaultsImport.hasOwnProperty(field.Id) ? (fieldDefaultsImport[field.Id]?.defaultWidth ?? 50) : 50);

        const colDef = {
          colId: field.Id,
          minWidth: 50,
          width: defaultWidth,
          resizable: true,
          pinned: false,
          suppressAutoSize: false,
          suppressSizeToFit: false,
          suppressMovable: true,
          filter: true,
          sortable: true,
          field: fieldName,
          headerName: (field.Project ?? field.Global),
          valueGetter: (params) => params.data?.[fieldName]?.Value,
          valueFormatter: (params) => this.getFieldValueFormatter(params, field),
          cellRenderer: (field.DataType === FieldDataTypeEnum.Boolean ? CheckboxCellRenderer : undefined),
          cellClassRules: (!previewMode ? {
            'fault-import-validation-error': (params) => (params.data?.[fieldName] as CellValidationResult)?.IsValid === +ValidationResult.Error,
            'fault-import-validation-warning': (params) => (params.data?.[fieldName] as CellValidationResult)?.IsValid === +ValidationResult.Warning
          } : undefined),
          tooltipValueGetter: params => {
            const cellValue = (params.data?.[fieldName] as CellValidationResult);
            if (cellValue?.ErrorCode && !previewMode) {
              return ValidationErrorCodes.ErrorCodes.hasOwnProperty(cellValue.ErrorCode) ? this.translateService.instant(ValidationErrorCodes.ErrorCodes[cellValue.ErrorCode]) : cellValue.ErrorCode;
            }
          }
        } as ColDef;
        colDefs.push(colDef);
      }
      else {
        if (fieldName === 'IsValid') {
          const colDef = {
            colId: fieldName,
            minWidth: 50,
            width: 80,
            resizable: true,
            pinned: true,
            suppressAutoSize: false,
            suppressSizeToFit: false,
            suppressMovable: true,
            // filter: true,
            sortable: true,
            field: fieldName,
            headerName: '',
            valueGetter: (params) => (params.data?.[fieldName] as CellValidationResult)?.IsValid,
            cellRenderer: ValidStatusCellRendererComponent,
            tooltipComponent: ValidTooltipComponent,
            tooltipValueGetter: params => {
              const cellValidationResults = Object.fromEntries(
                Object.keys(params.data)
                  .filter(f => f !== 'IsValid' && ((params.data?.[f] as CellValidationResult).IsValid === ValidationResult.Warning || (params.data?.[f] as CellValidationResult).IsValid === ValidationResult.Error))
                  .map(f => [f, (params.data?.[f] as CellValidationResult)])
              );

              // const fields = Object.keys(cellValidationResults).map(f => (this.faultsImportService.fieldDict.hasOwnProperty(f) ?
              //   (this.faultsImportService.fieldDict[f].Project ?? this.faultsImportService.fieldDict[f].Global) : f));
              //
              // const errorCodes = [...new Set(Object.values(cellValidationResults).map(err => err.ErrorCode))].map(err => ValidationErrorCodes.ErrorCodes.hasOwnProperty(err) ? this.translateService.instant(ValidationErrorCodes.ErrorCodes[err]) : err);

              const hasManyErrors = Object.keys(cellValidationResults).length > 5;
              const fields = Object.keys(cellValidationResults)
                .slice(0, 5)
                .map(f => {
                    return (this.faultsImportService.fieldDict.hasOwnProperty(f) ? `${(this.faultsImportService.fieldDict[f].Project ?? this.faultsImportService.fieldDict[f].Global)}: ` : (f !== 'RowIndex' ? `${f}: ` : ''))
                    + (ValidationErrorCodes.ErrorCodes.hasOwnProperty(cellValidationResults[f].ErrorCode) ? this.translateService.instant(ValidationErrorCodes.ErrorCodes[cellValidationResults[f].ErrorCode]) : cellValidationResults[f].ErrorCode);
                  }
                );

              if (hasManyErrors) {
                fields.push('...');
              }
              return ((fields?.length ?? 0) > 0 ? fields : null);
            },
            filter: ValidFilterComponent,
            filterParams: {
              showAllOption: false,
              filterValueGetter: (data) => {
                return data.IsValid.IsValid;
              },
              selectableOptions: [
                { id: ValidationResult.Warning, value: true, selected: true, icon: '_warning2' },
                { id: ValidationResult.Error, value: true, selected: true, icon: '_error2' },
              ]
            }
          } as ColDef;

          if (!previewMode) {
            colDefs.push(colDef);
          }
        }
        else if (fieldName === 'RowIndex') {
          const colDef = {
            colId: fieldName,
            minWidth: 80,
            width: 80,
            resizable: true,
            pinned: true,
            suppressAutoSize: false,
            suppressSizeToFit: false,
            suppressMovable: true,
            filter: true,
            sortable: true,
            field: fieldName,
            headerName: this.translateService.instant('MM.fault.import.columnHeaders.ExcelRow'),
            valueGetter: (params) => (params.data?.[fieldName] as CellValidationResult)?.Value
          } as ColDef;

          if (!previewMode) {
            colDefs.push(colDef);
          }
        }
        else {
          const key = Object.keys(ImportConstants.AdditionalFieldsdict).find(k => fieldName.toString().startsWith(k));
          if (key) {
            const colDef = {
              colId: fieldName,
              minWidth: 50,
              width: ImportConstants.AdditionalFieldsdict[key].DefaultWidth,
              resizable: true,
              pinned: false,
              suppressAutoSize: false,
              suppressSizeToFit: false,
              suppressMovable: true,
              filter: true,
              sortable: true,
              field: fieldName,
              headerName: fieldName,
              valueGetter: (params) => (params.data?.[fieldName] as CellValidationResult)?.Value,
              valueFormatter: (params) => this.getFieldValueFormatter(params, new FieldMinimal({DataType: ImportConstants.AdditionalFieldsdict[key].DataType})),
              cellClassRules: (!previewMode ?  {
                'fault-import-validation-error': (params) => (params.data?.[fieldName] as CellValidationResult)?.IsValid === +ValidationResult.Error,
                'fault-import-validation-warning': (params) => (params.data?.[fieldName] as CellValidationResult)?.IsValid === +ValidationResult.Warning
              } : undefined),
              tooltipValueGetter: params => {
                const cellValue = (params.data?.[fieldName] as CellValidationResult);
                if (cellValue?.ErrorCode && !previewMode) {
                  return ValidationErrorCodes.ErrorCodes.hasOwnProperty(cellValue.ErrorCode) ? this.translateService.instant(ValidationErrorCodes.ErrorCodes[cellValue.ErrorCode]) : cellValue.ErrorCode;
                }
              }
            } as ColDef;
            colDefs.push(colDef);
          }
          else {
            const colDef = {
              colId: fieldName,
              minWidth: 80,
              width: 80,
              resizable: true,
              pinned: false,
              suppressAutoSize: false,
              suppressSizeToFit: false,
              suppressMovable: true,
              filter: true,
              sortable: true,
              field: fieldName,
              headerName: fieldName,
              valueGetter: (params) => params.data?.[fieldName]?.Value,
              // valueFormatter: (params) => this.getFieldValueFormatter(params, field),
              cellClassRules: (!previewMode ? {
                'fault-import-validation-error': (params) => (params.data?.[fieldName] as CellValidationResult)?.IsValid === +ValidationResult.Error,
                'fault-import-validation-warning': (params) => (params.data?.[fieldName] as CellValidationResult)?.IsValid === +ValidationResult.Warning
              } : undefined),
              tooltipValueGetter: params => {
                const cellValue = (params.data?.[fieldName] as CellValidationResult);
                if (cellValue?.ErrorCode && !previewMode) {
                  return ValidationErrorCodes.ErrorCodes.hasOwnProperty(cellValue.ErrorCode) ? this.translateService.instant(ValidationErrorCodes.ErrorCodes[cellValue.ErrorCode]) : cellValue.ErrorCode;
                }
              }
            } as ColDef;
            colDefs.push(colDef);
          }
        }
      }
    }

    return colDefs;
  }

  private getFieldValueFormatter(params: ValueFormatterParams, field: FieldMinimal) {
    switch (field.DataType) {
      case FieldDataTypeEnum.Date:
      case FieldDataTypeEnum.DateTime:
        if (field.DataType === FieldDataTypeEnum.Date || ['Date1', 'Date2', 'Date3'].includes(field?.Id))
        {
          return this.datepipe.transform(params.value, 'shortDate');
        }
        else {
          return this.datepipe.transform(params.value, 'short');
        }

      case FieldDataTypeEnum.Decimal:
      case FieldDataTypeEnum.Double:
        if (field.Id !== FieldDefaultNameEnum.Number) {
          if (params.value) {
            const thousandsSeparator = getLocaleNumberSymbol(this.translateService.currentLang, NumberSymbol.CurrencyGroup);
            return this.numberpipe.transform(params.value)?.replace(new RegExp(`\\${thousandsSeparator}`, 'g'), '');
          }
        }
    }
  }
}
